import { useMemo, useRef, useState, useEffect } from 'react';
import clsx from 'clsx';
import AddIcon from '@mui/icons-material/Add';
import useOnClickOutside from 'use-onclickoutside';
import { usePopupPosition } from '../../hooks';
import Chip from '../Chip';
import { Input } from '../Input';
import { SearchIcon } from '../SearchBar/SearchIcon';
import { Spinner } from '../Spinner';

export type OptionType<T = string> = {
  value: T;
  label: string;
};

type MultiSelectProps<T extends string> = {
  options: OptionType<T>[];
  label?: string;
  id?: string;
  value?: T[];
  onChange?: (newValues: T[], addedOption?: OptionType<T>) => void;
  onBeforeRemove?: (option: OptionType<T>, nextValues: T[]) => boolean | void;
};

export function MultiSelect<T extends string>({
  options,
  label,
  id,
  value,
  onChange,
  onBeforeRemove,
}: MultiSelectProps<T>) {
  const [selectedOptions, setSelectedOptions] = useState<OptionType<T>[]>(
    value?.map(v => ({
      value: v,
      label: options.find(c => c.value === v)?.label || v,
    })) || []
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const expectedSelectedOptions = useMemo(() => {
    if (!value || value.length === 0) return [];
    return value.map(v => ({
      value: v,
      label: options.find(c => c.value === v)?.label || v,
    }));
  }, [value, options]);

  const areAllLabelsResolved = useMemo(() => {
    if (!value || value.length === 0) return true;
    return value.every(v => {
      const option = options.find(c => c.value === v);

      return !!option;
    });
  }, [value, options]);

  const prevValueRef = useRef<string>('');
  const prevOptionsRef = useRef<OptionType<T>[]>([]);

  useEffect(() => {
    const valueKey = (value || []).sort().join(',');
    const optionsKey = options
      .map(o => `${o.value}:${o.label}`)
      .sort()
      .join('|');
    const prevValueKey = prevValueRef.current;
    const prevOptionsKey = prevOptionsRef.current
      .map(o => `${o.value}:${o.label}`)
      .sort()
      .join('|');

    if (valueKey !== prevValueKey || optionsKey !== prevOptionsKey) {
      setSelectedOptions(expectedSelectedOptions);
      prevValueRef.current = valueKey;
      prevOptionsRef.current = options;
    }
  }, [value, options, expectedSelectedOptions]);

  const refAnchorRef = useRef<any>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const {
    popupRef: positionPopupRef,
    anchorRef,
    popupPosition,
    anchorPosition,
  } = usePopupPosition();

  const handleSelect = (option: OptionType<T>) => {
    const updated = [...selectedOptions, option];
    setSelectedOptions(updated);
    onChange?.(
      updated.map(o => o.value),
      option
    );
    setSearchTerm('');
  };

  const handleRemove = (option: OptionType<T>) => {
    const updated = selectedOptions.filter(item => item.value !== option.value);
    const nextValues = updated.map(o => o.value);
    if (onBeforeRemove?.(option, nextValues) === false) return;
    setSelectedOptions(updated);
    onChange?.(nextValues);
  };

  const availableOptions = useMemo(
    () =>
      options.filter(
        option =>
          !selectedOptions.some(selected => selected.value === option.value) &&
          option.label.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [options, selectedOptions, searchTerm]
  );

  const handleClose = () => {
    setDropdownOpen(false);
  };

  useOnClickOutside(refAnchorRef, e => {
    if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
      handleClose();
    }
  });

  return (
    <div className="relative w-full font-sans">
      {label && (
        <label htmlFor={id} className="block mb-2 font-semibold text-gray-800">
          {label}
        </label>
      )}

      <div
        ref={node => {
          node && anchorRef(node);
          refAnchorRef.current = node;
        }}
        onClick={() => setDropdownOpen(true)}
        className={clsx(
          'min-h-[42px] max-h-[168px] flex flex-wrap items-center gap-2 px-3 py-2 border border-gray-300 rounded-xl bg-white cursor-text overflow-y-auto',
          dropdownOpen ? 'ring-2 ring-blue-500' : ''
        )}
      >
        {!areAllLabelsResolved && value && value.length > 0 ? (
          <div className="flex items-center gap-2 text-gray-500">
            <Spinner size={16} className="h-6 w-6" />
            <span className="text-tiny">Loading...</span>
          </div>
        ) : (
          areAllLabelsResolved &&
          selectedOptions.map(option => (
            <Chip
              key={option.value}
              label={option.label}
              onClick={() => handleRemove(option)}
              className="bg-gray-100"
            />
          ))
        )}
      </div>

      {dropdownOpen && (
        <div
          ref={node => {
            node && positionPopupRef(node);
            popupRef.current = node;
          }}
          style={{
            position: 'fixed',
            left: popupPosition?.style?.left ?? 0,
            top: popupPosition?.style?.top ?? 0,
            minWidth: anchorPosition.width ?? '100%',
            maxWidth: '200px',
          }}
          className="z-popper mt-1"
        >
          <div className="rounded-xl border border-gray-300 bg-white shadow-lg p-4">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col">
                <div onClick={e => e.stopPropagation()}>
                  <Input
                    type="text"
                    size="small"
                    label=""
                    placeholder="Search..."
                    value={searchTerm}
                    prefix={
                      <SearchIcon className="w-6 h-6 flex self-center mt-0 z-10 text-gray-400" />
                    }
                    labelClassName="text-secondary text-tiny sm:text-small font-semibold"
                    inputClassName="pl-12 color-gray-400 rounded-full relative sm:text-small text-tiny"
                    onChange={value => setSearchTerm(value)}
                    className="w-full rounded-full text-tiny sm:text-small focus:outline-primary"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-2 max-h-48 min-h-38 sm:max-h-72 overflow-y-auto">
                {availableOptions.map(option => (
                  <div
                    key={option.value}
                    className="flex items-center justify-between px-2 sm:py-2 py-0 hover:bg-gray-100 rounded-md cursor-pointer"
                    onClick={e => {
                      e.stopPropagation();
                      handleSelect(option);
                    }}
                  >
                    <span className="truncate text-tiny sm:text-medium">
                      {option.label}
                    </span>
                    <div className="text-main hover:bg-main hover:text-contrast rounded-xs p-1">
                      <AddIcon
                        fontSize="large"
                        color="inherit"
                        className="cursor-pointer"
                      />
                    </div>
                  </div>
                ))}
                {!availableOptions.length && (
                  <div className="text-gray-400 text-center text-medium sm:text-tiny py-4">
                    No results
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
